import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Clock, GraduationCap, CheckCheck, CalendarClock, Eye, EyeOff } from "lucide-react";
import { api, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ICON = { task: Clock, record: CalendarClock, training: GraduationCap };
const SEV = {
  high: "border-red-200 bg-red-50 text-red-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-slate-200 bg-slate-50 text-slate-600",
};

export default function Notifications() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [hideRead, setHideRead] = useState(false);

  const load = () => api.get("/notifications").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read", { all: true });
    toast.success("All caught up");
    load();
    window.dispatchEvent(new Event("esp26-notifications-updated"));
  };

  const openItem = async (it) => {
    await api.post("/notifications/read", { keys: [it.key] });
    window.dispatchEvent(new Event("esp26-notifications-updated"));
    if (it.employee_id) navigate(`/people/${it.employee_id}`);
    else navigate("/work");
  };

  const visible = hideRead ? data.items.filter((it) => !it.read) : data.items;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Notifications</h1>
          <p className="mt-1 text-sm text-slate-500">{data.unread} unread · {data.items.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="toggle-hide-read-button" onClick={() => setHideRead((v) => !v)} variant="outline">
            {hideRead ? <><Eye className="mr-1.5 h-4 w-4" /> Show all</> : <><EyeOff className="mr-1.5 h-4 w-4" /> Hide read</>}
          </Button>
          <Button data-testid="mark-all-read-button" onClick={markAll} variant="outline">
            <CheckCheck className="mr-1.5 h-4 w-4" /> Mark all read
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {visible.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 py-16 text-center">
            <Bell className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-400">{hideRead ? "No unread notifications." : "You're all caught up."}</p>
          </div>
        )}
        {visible.map((it) => {
          const Icon = ICON[it.type] || Bell;
          return (
            <button
              key={it.key}
              data-testid="notification-item"
              onClick={() => openItem(it)}
              className={`flex w-full items-center gap-3 rounded-xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md ${it.read ? "opacity-60" : ""}`}
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg border ${SEV[it.severity]}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-heading text-sm font-semibold text-slate-900">{it.title}</p>
                  {!it.read && <span className="h-2 w-2 rounded-full bg-orange-500" />}
                </div>
                <p className="truncate text-sm text-slate-500">{it.detail}</p>
              </div>
              {it.date && <span className="whitespace-nowrap text-xs text-slate-400">{shortDate(it.date)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
